import React from 'react';

import { Button } from 'components/Button';

import CustomModal from 'components/CustomModal';
import { StyledTextField } from 'components/Input/styles';

import { VersionBadge, VersionLabel } from './styles';

interface SaveVersionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (versionName: string) => void;
  isSaving: boolean;
  nextVersionNumber: number;
}

const SaveVersionModal: React.FC<SaveVersionModalProps> = ({
  open,
  onClose,
  onConfirm,
  isSaving,
  nextVersionNumber,
}) => {
  const [versionName, setVersionName] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setVersionName('');
    }
  }, [open]);

  const handleConfirm = (): void => {
    const customName = versionName.trim();
    const name = customName ? `v${nextVersionNumber} - ${customName}` : `v${nextVersionNumber}`;
    onConfirm(name);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' && !isSaving) {
      handleConfirm();
    }
  };

  return (
    <CustomModal
      openModal={open}
      modalTitle="Publish"
      modalDescription="Publish your changes as a new version. You can name it and restore earlier versions any time."
      onClose={onClose}
      modalWidth="sm"
      cancelButton={
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
      }
      submitButton={
        <Button variant="contained" color="primary" onClick={handleConfirm} disabled={isSaving}>
          {isSaving ? 'Publishing...' : 'Publish'}
        </Button>
      }
    >
      <VersionLabel>
        New version: <VersionBadge>v{nextVersionNumber}</VersionBadge>
      </VersionLabel>
      <StyledTextField
        fullWidth
        value={versionName}
        onChange={(e) => setVersionName(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        placeholder="Version name (optional)"
        autoFocus
        data-testid="version-name-input"
      />
    </CustomModal>
  );
};

export default SaveVersionModal;
